import React, { useEffect, useState } from "react";
import { useSearchParams, useNavigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import MarketingLayout from "@/components/MarketingLayout";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import * as Lucide from "lucide-react";
import { toast } from "sonner";

export default function SetPasswordPage() {
  const [params] = useSearchParams();
  const nav = useNavigate();
  const { loginWithToken } = useAuth();
  const token = params.get("token") || "";
  const [loading, setLoading] = useState(true);
  const [target, setTarget] = useState(null);
  const [error, setError] = useState(null);
  const [pw, setPw] = useState("");
  const [pw2, setPw2] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!token) { setError("Missing token in URL."); setLoading(false); return; }
    api.get(`/auth/password-token/${token}`)
      .then((r) => setTarget(r.data))
      .catch((e) => setError(e?.response?.data?.detail || "Invalid link"))
      .finally(() => setLoading(false));
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    if (pw.length < 8) { toast.error("Password must be at least 8 characters"); return; }
    if (pw !== pw2) { toast.error("Passwords don't match"); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/auth/set-password", { token, password: pw });
      if (loginWithToken) {
        await loginWithToken(data.token);
      } else {
        localStorage.setItem("binarex_token", data.token);
      }
      toast.success("Password set. You're logged in.");
      nav("/dashboard/support");
    } catch (e2) {
      toast.error(e2?.response?.data?.detail || "Failed to set password");
    } finally { setBusy(false); }
  };

  return (
    <MarketingLayout>
      <section className="max-w-md mx-auto px-5 py-16">
        <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} className="bx-card p-8" data-testid="set-password-card">
          <div className="text-[11px] uppercase tracking-[0.2em] text-slate-500">Welcome</div>
          <h1 className="bx-heading text-3xl font-extrabold mt-2">Set your password</h1>
          {loading && <div className="mt-6 text-slate-400 text-sm flex items-center gap-2"><Lucide.Loader2 className="animate-spin" size={14} /> Verifying link…</div>}
          {!loading && error && (
            <div className="mt-6">
              <div className="bg-rose-500/10 border border-rose-500/30 text-rose-200 p-4 rounded-lg text-sm" data-testid="set-password-error">
                <Lucide.AlertTriangle size={14} className="inline mr-1.5" /> {error}
              </div>
              <Link to="/login" className="text-purple-300 underline text-sm mt-4 inline-block">Go to login</Link>
            </div>
          )}
          {!loading && target && (
            <form onSubmit={submit} className="mt-6 space-y-4">
              <div className="text-sm text-slate-400">
                For <span className="text-slate-100 font-mono">{target.email}</span>
              </div>
              <div>
                <label className="text-xs text-slate-400">New password</label>
                <input data-testid="set-password-input" type="password" minLength={8} value={pw} onChange={(e) => setPw(e.target.value)} placeholder="At least 8 characters" className="bx-input mt-1" required />
              </div>
              <div>
                <label className="text-xs text-slate-400">Confirm password</label>
                <input data-testid="set-password-confirm" type="password" minLength={8} value={pw2} onChange={(e) => setPw2(e.target.value)} className="bx-input mt-1" required />
              </div>
              <button type="submit" data-testid="set-password-submit" disabled={busy} className="bx-btn-primary w-full justify-center disabled:opacity-50">
                {busy ? <><Lucide.Loader2 className="animate-spin" size={14} /> Setting…</> : <><Lucide.KeyRound size={14} /> Set password & sign in</>}
              </button>
            </form>
          )}
        </motion.div>
      </section>
    </MarketingLayout>
  );
}
